import { Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { FILLED_TOGGLE_CONTROL_HEIGHT, isValidDate } from "@shared";

import { CounselorSelect } from "@/common/components/counselor-select/CounselorSelect";
import { TakeoverView } from "@/pages/takeover/Takeover.types";
import { useTranslation } from "@/translations";

import { TakeoverViewToggle } from "../view-toggle/TakeoverViewToggle";

const COUNSELOR_FIELD_MIN_WIDTH = 220;
const DATE_FIELD_MIN_WIDTH = 180;

interface Props {
  view: TakeoverView;
  toTakeOverCount: number;
  onViewChange: (view: TakeoverView) => void;
  counselorId: string | null;
  onCounselorChange: (counselorId: string | null) => void;
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateFromChange: (value: Date | null) => void;
  onDateToChange: (value: Date | null) => void;
}

/**
 * Tab switch plus the per-tab filters: the takeover tab narrows the released appointments by
 * counselor, the release tab picks whose appointments to release and over which period.
 */
export function TakeoverFilterBar({
  view,
  toTakeOverCount,
  onViewChange,
  counselorId,
  onCounselorChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}: Props) {
  const { t } = useTranslation();
  const isRelease = view === "toRelease";

  return (
    <Box display="flex" alignItems="center" gap={3}>
      <TakeoverViewToggle value={view} toTakeOverCount={toTakeOverCount} onChange={onViewChange} />
      <CounselorSelect
        selectedCounselor={counselorId}
        onSelectCounselor={onCounselorChange}
        // Releasing always happens for exactly one counselor, so that tab offers no "all" entry.
        includeAllOption={!isRelease}
        allOptionLabel={t("takeover.filter.allCounselors")}
        ariaLabel={t("takeover.filter.counselorAriaLabel")}
        minWidth={COUNSELOR_FIELD_MIN_WIDTH}
        height={FILLED_TOGGLE_CONTROL_HEIGHT}
      />
      {isRelease && (
        <>
          <DatePicker
            label={t("takeover.filter.dateFrom")}
            value={dateFrom}
            onChange={onDateFromChange}
            slotProps={{
              field: { clearable: true },
              textField: { size: "small", sx: { minWidth: DATE_FIELD_MIN_WIDTH } },
            }}
          />
          <DatePicker
            label={t("takeover.filter.dateTo")}
            value={dateTo}
            onChange={onDateToChange}
            minDate={isValidDate(dateFrom) ? dateFrom : undefined}
            slotProps={{
              field: { clearable: true },
              textField: { size: "small", sx: { minWidth: DATE_FIELD_MIN_WIDTH } },
            }}
          />
        </>
      )}
    </Box>
  );
}
